import {I_TABLE_ITEM,I_OPTION} from '../tsx/components';
import I_SKILL from '../services/api/interfaces/I_SKILLS';
import I_ACTIVITY_TYPE from '../services/api/interfaces/I_ACTIVITY_TYPES';
import {I_CUSTOMER_REFERENTIAL} from '../services/api/interfaces/I_CUSTOMERS';
import {getReferentialLabel} from './labelHelper';
import {I_ORGANISATION, I_POLE, I_REFERENTIAL} from '../services/api/interfaces/I_HELLO';
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import I_ORGANISATIONS_SET from 'services/api/interfaces/I_ORGANISATIONS';

export const getQuarterLabel = (dateStart:Date) => {
  const month1 = (new Date(dateStart));
  const month3 = (new Date(dateStart));
  month3.setMonth(month3.getMonth() + 2);
  return `${format(month1, "MMM yyyy", { locale: fr })} - ${format(month3, "MMM yyyy", { locale: fr })}`;
};

export const getWeekLabel = (dateStart:Date) => {
  return `Semaine du ${format(dateStart, "PP", { locale: fr })}`;
};

// Format date to yyyyMMdd_HHmm format
export const formatDateTime = (date: Date, locale = 'fr') => {

  // use formatToParts() to convert a date and put it into an array for the different parts of the date
  const customDateArray =
          new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' })
              .formatToParts(date)

  //This converts the array to an object to make it easier to pick out what you want using template literals.
  let dateParts: {[key:string]:string} = {}
  customDateArray.forEach(({type, value}) => {
    dateParts[type] = value
  })

  return `${dateParts.year}${dateParts.month}${dateParts.day}_${dateParts.hour}${dateParts.minute}`
}

/**
 * Formate en string un nombre selon la locale et l'unité
 * @param value
 * @param locale
 * @param unit
 * @param notation
 * @returns
 */
export const formatNumber = ({value,locale='fr',unit, notation = "compact"}:
                                 {value?:number, locale?:string, unit?:'percent'|'hour'|'day'|'hourbig'|'hourbighidden',
                                   notation?:"standard" | "compact"}):any => {
  if (value === undefined) {
    return '';
  }

  if ((unit === 'hour' && value <= 9999) || (unit === 'hourbig' && value <= 99999)) {
    return new Intl.NumberFormat(locale, {style:'unit',unit:'hour',notation:'standard'})
        .format(value)
  }
  else if (unit === 'hour' && value > 9999) {
    return new Intl.NumberFormat(locale,
        {style:'unit',unit:'hour',maximumFractionDigits:2, maximumSignificantDigits:4, notation})
        .format(value).replace('k','K').replace(/\u202f/g,'');
  }
  else if (unit === 'hourbig' && value > 99999) {
    return new Intl.NumberFormat(locale, {style:'unit',unit:'hour',maximumFractionDigits:0,maximumSignificantDigits:3, notation})
        .format(value).replace('k','K').replace(/\u202f/g,'');
  }

  if (unit === 'percent') {
    return new Intl.NumberFormat(locale, {style:'percent',maximumFractionDigits:2,notation})
        .format(value/100);
  }
  if (unit === 'day') {
    return new Intl.NumberFormat(locale, {style:'unit',unit:'day',maximumFractionDigits:2,notation})
        .format(value);
  }

  return new Intl.NumberFormat(locale, {maximumFractionDigits:2,notation})
        .format(value);

};

/**
 * Fonction de tri selon un attribut
 * @param a
 * @param b
 * @param sortKey
 * @param sortAsc
 * @returns
 */
export const orderDataTable = (a:I_TABLE_ITEM, b:I_TABLE_ITEM, sortKey:string, sortAsc:boolean) => {
  const aV = a.values[sortKey].sortableValue !== undefined ? a.values[sortKey].sortableValue : a.values[sortKey].value;
  const bV = b.values[sortKey].sortableValue !== undefined ? b.values[sortKey].sortableValue : b.values[sortKey].value;
  return aV < bV ? (sortAsc ? -1 : 1) : aV > bV ? (sortAsc ? 1 : -1) : 0
};

/**
 * Formatte un array de compétences en liste de labels séparés par des virgules
 * @param array
 * @returns
 */
export const formatSkillsList = (array:I_SKILL[]) => {
  return array.map(v => {
    return decodeURIComponent(v.name);
  }).join(', ');
};

/**
 * Formatte un array d'argement d'organisme en liste de labels séparés par des virgules
 * @param array
 * @returns
 */
export const formatReferentialsList = (array?:I_CUSTOMER_REFERENTIAL[],allReferentials?:I_REFERENTIAL[]) => {
  if (!array) {
    return '';
  }
  return array.map(v => {
    const label = getReferentialLabel({referential:v.name,allReferentials});
    return `${v.number ? ` ${v.number}` : ''}`;
  }).join(', ');
};

/**
 * Recupère la liste des id de categories tous niveaux confondus
 * @param array
 * @returns
 */
export const getTreeCategoriesID = (array?:any[]):number[] => {
  if (!array) {
    return [];
  }
  let all:number[] = [];
  array.forEach(s => {
    const id = s.id;
    const arraySubIds:number[] = s.children ? s.children?.map((ss:any) => ss.id) : [];
    arraySubIds?.unshift(id);
    all = all.concat(arraySubIds);
  });
  return all;
};

/**
 * Recupère la liste des id de skill tous niveaux confondus
 * @param array
 * @returns
 */
export const getAllSkillsID = (array?:I_SKILL[]):number[] => {
  return getTreeCategoriesID(array);
};


/**
 * Recupère la liste des id de activity-type tous niveaux confondus
 * @param array
 * @returns
 */
export const getAllActivityTypesID = (array?:I_ACTIVITY_TYPE[]):number[] => {
  return getTreeCategoriesID(array);
};


/**
 * Format d'un item de données
 * I_SKILL | I_ACTIVITY_TYPE
 */
interface I_TREE_DATA {
  /** id */
  id: number;
  name: string;
  description: string;
  children?: I_TREE_DATA[];
}
/**
 * Construit un array d'option de select à partir d'un arbre de données
 * listData est un array d'object, qui peuvent avoir chacun un attribut children
 * levelMax est le niveau max de profondeur (1er niveau = 0). -1 si pas de restriction de niveau
 * parentID est l'id de l'option parente (niveau 1 uniquement), pour ne recuperer que les options associées
 * => skills
 * => activity types
 */
export const buildOptionsFromTree = ({
  listData,
  levelMax,
  parentID,
  forceParentID,
}:{
  listData:I_TREE_DATA[],
  levelMax:number,
  parentID?:number|string,
  forceParentID?:boolean,
}) => {
  if (!listData) {
    return [];
  }
  return _buildOptionLevel(listData, 0, levelMax, parentID, forceParentID);
};

const _buildOptionLevel = (items:I_TREE_DATA[],level:number,levelMax:number,parentID?:number|string,forceParentID?:boolean):I_OPTION[] => {
  if (!items || items.length <= 0) {
    return [];
  }
  if (levelMax !== -1 && level > levelMax) {
    return [];
  }
  let builded:I_OPTION[] = [];
  items.forEach((item:I_TREE_DATA) => {
    // si on veut uniquement les fils d'un parent
    if (!!parentID) {
      if (item.id === parentID) {
        // on prend que ses children
        const childrenOptions:I_OPTION[] = item.children && item.children.length > 0 ? _buildOptionLevel(item.children,level + 1,levelMax) : [];
        builded = childrenOptions;
      }
    }
    // si pas de parent, mais qu'on veut desactiver les choix
    else if (forceParentID) {
      return [];
    }
    // si on ne filtre pas
    else {
      const option:I_OPTION = _getOptionItem(item,level);
      // si l'item a des children
      const childrenOptions:I_OPTION[] = item.children && item.children.length > 0 ? _buildOptionLevel(item.children,level + 1,levelMax) : [];
      builded = builded.concat([option].concat(childrenOptions));
    }
  });
  return builded;
};
const _getOptionItem = (item:I_TREE_DATA,level:number) => {
  return {
    value: item.id+'',  // on force la valeur en string pour les options de select
    name: decodeURIComponent(item.name),
    level,
  };
};

/**
 * Renvoie true si l'extension du fichier passé est valide, ou si undefined
 * @param param0
 * @returns
 */
export const isExtensionValid = ({file,validityArray}:{file?:File,validityArray:string[]}):boolean => {
  if (!file) {
    return true;
  }
  const fileExtension = file.name.split('.').pop();
  if (!fileExtension || !validityArray.find(e => e.includes(fileExtension))) {
    return false;
  }
  return true;
};


export interface I_PAGINATED_RESULTS {
  count: number;
  items: any[];
}
/**
 * Calcule le nombre de pages selon le resultat et la pagination
 * @param param0
 * @returns
 */
export const getTotalPages = ({
  listData,
  pagination_limit,
  paginationUserLimit,
}:{
  listData?:I_PAGINATED_RESULTS,
  pagination_limit?:number,
  paginationUserLimit?:number,
}) => {
  if (!listData) {
    return 0;
  }
  const LIMIT = paginationUserLimit !== undefined ? paginationUserLimit : (pagination_limit || 5);
  return Math.ceil(listData.count / LIMIT);
};


export const getAPIPage = ({
  currentPage,
  pagination_limit,
  paginationUserLimit,
}:{
  currentPage: number,
  pagination_limit?:number,
  paginationUserLimit?:number,
}) => {
  // Le nombre de page custom par page API est pagination_limit / paginationUserLimit.
  // On parti du principe que paginationUserLimit est un diviseur entier de pagination_limit.
  const userPagesCountByAPIPage = Math.ceil((pagination_limit || 250) / (paginationUserLimit || 50));
  // Le nombre de page custom par page API est la partie supérieure de ce rapport.
  return Math.ceil(currentPage / userPagesCountByAPIPage);
};

export const getSubPage = ({
  currentPage,
  pagination_limit,
  paginationUserLimit,
}:{
  currentPage: number,
  pagination_limit?:number,
  paginationUserLimit?:number,
}) => {
  // Le nombre de page custom par page API est pagination_limit / paginationUserLimit.
  // On parti du principe que paginationUserLimit est un diviseur entier de pagination_limit.
  const userPagesCountByAPIPage = Math.ceil((pagination_limit || 250) / (paginationUserLimit || 50));
  return currentPage - (Math.floor(currentPage / userPagesCountByAPIPage) * userPagesCountByAPIPage);
};


export const extractSearchableValue = ({
  level,item,attrName,parentItem,parentItems
}:{level:number,item:any,attrName:string,parentItem?:any,parentItems?:any}) => {
  // si un niveau 1 matche, on veut voir tous ses niveaux 2
  // si un niveau 2 matche, on voir son niveau 1
  if (level === 1) {
    return ((decodeURIComponent(item[attrName]) || '') + (item.children?.map((i2:any) => {
      return i2[attrName] + (i2.children?.map((i3:any) => {
        return i3[attrName] + (i3.children?.map((i4:any) => {
          return i4[attrName] + (i4.children?.map((i5:any) => {
            return i5[attrName]; // stop fafufi comme fa
          }).join(' '));
        }).join(' '));
      }).join(' '));
    }).join(' '))).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  else if (level === 2) {
    const parentValue = parentItem ? decodeURIComponent(parentItem.name) + '/' : '';
    const itemValue = decodeURIComponent(item.name) || '';
    const childrenValues = item.children?.map((i3:any) => {
      return i3.name + (i3.children?.map((i4:any) => {
        return i4.name + (i4.children?.map((i5:any) => {
          return i5.name;
        }).join(' '));
      }).join(' '));
    }).join(' ');
    return (parentValue + itemValue + childrenValues).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  else if (level === 3) {
    const parentsValue = parentItems.map((i:any) => decodeURIComponent(i.name)).join('/');
    const parentValue = parentItem ? decodeURIComponent(parentItem.name) + '/' : '';
    const itemValue = decodeURIComponent(item.name) || '';
    const childrenValues = item.children?.map((i4:any) => {
      return i4.name + (i4.children?.map((i5:any) => {
        return i5.name;
      }).join(' '));
    }).join(' ');
    return (parentsValue + parentValue + itemValue + childrenValues).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  else if (level === 4) {
    const parentsValue = parentItems.map((i:any) => decodeURIComponent(i.name)).join('/');
    const parentValue = parentItem ? decodeURIComponent(parentItem.name) + '/' : '';
    const itemValue = decodeURIComponent(item.name) || '';
    const childrenValues = item.children?.map((i5:any) => {
      return i5.name;
    }).join(' ');
    return (parentsValue + parentValue + itemValue + childrenValues).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  else if (level === 5) {
    const parentsValue = parentItems.map((i:any) => decodeURIComponent(i.name)).join('/');
    const parentValue = parentItem ? decodeURIComponent(parentItem.name) + '/' : '';
    const itemValue = decodeURIComponent(item.name) || '';
    return (parentsValue + parentValue + itemValue).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  else {
    return '';
  }
  // stop fafufi comme fa
};



export const matchOneRoleFunction = ({
  requireRoleFunction,
  role,
  fonction,
}:{
  requireRoleFunction: string[],
  role?: string,
  fonction?: string,
}) => {
  if (!requireRoleFunction || requireRoleFunction.length === 0) {
    return true;
  }
  if (!role) {
    console.log('Missing role for user')
    return false;
  }
  let matchesOne = false;
  requireRoleFunction.forEach((req:string) => {
    if (req.indexOf('-') === -1) {
      // require only role
      if (req === role){
        matchesOne = true;
        console.log(`Found required role ${role}`);
      }
      else {
        console.log(`Required role ${req} not matching with ${role}`);
      }
    }
    else {
      const requiredRole = req.split('-')[0];
      const requiredFunction = req.split('-')[1];
      // uniquement role
      if (!requiredFunction || requiredFunction === '*') {
        if (requiredRole === role){
          matchesOne = true;
          console.log(`Found required role ${role}`);
        }
        else {
          console.log(`Required role ${requiredRole} not matching with ${role}`);
        }
      }
      // require role + function
      else {
        if (requiredRole === role && !!fonction && requiredFunction === fonction){
          matchesOne = true;
          console.log(`Found required role-function ${role}`);
        }
        else {
          console.log(`Required role-function ${requiredRole} ${requiredFunction} not matching with ${role} ${fonction}`);
        }
      }
    }
  });
  return matchesOne;
};

export const getDefaultUserOrganisation = ({
  organisationsSets,
  key,
  selectedDirection,
}:{
  organisationsSets?: I_ORGANISATIONS_SET[],
  key: string,
  selectedDirection?: string,
}):{
  defaultDirection?: I_ORGANISATION,
  defaultPole?: I_POLE,
} => {
  // on veut le premier set d'organisations dont une des key est setKey
  const matchingSet = organisationsSets?.find((set:I_ORGANISATIONS_SET) => set.filters.find((name:string) => name === key) !== undefined);
  // si la direction actuellement selectionnee est dans sa liste, on la garde
  if (!!selectedDirection && matchingSet?.organisations?.map(o => o.key)?.indexOf(selectedDirection) !== -1) {
    const defaultDirection = matchingSet?.organisations.find((dir:I_ORGANISATION) => dir.key === selectedDirection);
    const defaultPole = defaultDirection?.poles.find((pole:I_POLE) => pole.default === true);
    return {
      defaultDirection,
      defaultPole,
    };
  }
  // sinon on prend la premiere par defaut
  else {
    const defaultDirection = matchingSet?.organisations.find((dir:I_ORGANISATION) => dir.default === true);
    const defaultPole = defaultDirection?.poles.find((pole:I_POLE) => pole.default === true);
    return {
      defaultDirection,
      defaultPole,
    };
  }
};

export const isValidUserOrganisationSelections = ({
  organisationsSets,
  key,
  selections,
  selection,
}:{
  organisationsSets?: I_ORGANISATIONS_SET[],
  key: string,
  selections?: string[],
  selection?: string,
}):boolean => {
  // aucune selection actuellement, on doit appliquer les nouvelles
  if ((!selections || selections.length === 0) && (!selection || selection === '')) {
    return false;
  }
  // on veut le premier set d'organisations dont une des key est setKey
  const matchingSet = organisationsSets?.find((set:I_ORGANISATIONS_SET) => set.filters.find((name:string) => name === key) !== undefined);
  const allowedDirectionsKeys = matchingSet?.organisations.map((dir:I_ORGANISATION) => dir.key);
  if (selections && selections.length > 0) {
    const firstInvalidOne = selections.find((s:string) => allowedDirectionsKeys?.indexOf(s) === -1);
    return firstInvalidOne === undefined;
  }
  if (selection && selection !== '') {
    return !!allowedDirectionsKeys && allowedDirectionsKeys.indexOf(selection) !== -1;
  }
  return true;
};

/**
 * Get the formatted date of the last day of the month of the date, as yyyy-MM-dd
 * @param date
 * @returns
 */
export const getFormattedDateEndOfMonth = (date:Date) => {
  const monthNumber = date.getMonth() + 1
  return format(date, "yyyy-MM-", { locale: fr }) +
    (monthNumber === 2 ? '28' :
      (monthNumber === 1 || monthNumber === 3  || monthNumber === 5 || monthNumber === 7 ||
      monthNumber === 8 || monthNumber === 10 || monthNumber === 12 ? '31' : '30'));
}
